import * as React from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  FloatingArrow,
  hide,
  useTransitionStyles
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import { arrow } from '@floating-ui/dom';
import { RefObject, useEffect, useRef } from 'react';
import { CloseIcon } from '@navi/web-ui/lib/icons';
import { GenericObject } from '@cp/src/types/CommonConstans';
import cx from 'classnames';

interface PopperOptions {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  arrowRef?: RefObject<any>;
  hiddenPadding?: number;
  openOnClick?: boolean;
  mainAxisOffset?: number;
  crossAxisOffset?: number;
  disableCloseOnOutsideClick?: boolean;
  transform?: boolean;
  hideStrategy?: 'escaped' | 'referenceHidden' | undefined;
}

interface PopperContentProps {
  arrowColor?: string;
  noHeader?: boolean;
  headerText?: string;
  iconRequired?: boolean;
  onClose?: () => void;
  customClose?: boolean;
  children?: React.ReactNode | ((params: GenericObject) => React.ReactNode);
  className?: string;
  style?: React.CSSProperties;
  showArrow?: boolean;
}

export enum Directions {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
  NONE = 'none'
}

export function usePopper({
  initialOpen = false,
  placement = 'top',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  arrowRef,
  hiddenPadding = 16,
  openOnClick = true,
  mainAxisOffset = 9,
  crossAxisOffset = 9,
  disableCloseOnOutsideClick = false,
  transform = true,
  hideStrategy = 'escaped'
}: PopperOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    transform,
    middleware: [
      offset({
        mainAxis: mainAxisOffset,
        crossAxis: crossAxisOffset
      }),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef?.current }),
      hide({
        padding: hiddenPadding,
        strategy: hideStrategy
      })
    ]
  });

  const context = data.context;

  const click = useClick(context, {
    enabled: controlledOpen == null
  });
  const dismiss = useDismiss(context, {
    outsidePress: !disableCloseOnOutsideClick,
    escapeKey: !disableCloseOnOutsideClick
  });
  const role = useRole(context, { role: 'popper' });

  const defaultInteractions = [dismiss, role];

  if (openOnClick) {
    defaultInteractions.push(click);
  }

  const interactions = useInteractions(defaultInteractions);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data
    }),
    [open, setOpen, interactions, data]
  );
}

export type PopperContextType = ReturnType<typeof usePopper> | null;

const PopperContext = React.createContext<PopperContextType>(null);

export const usePopperContext = () => {
  const context = React.useContext(PopperContext);

  if (context == null) {
    throw new Error('Popper components must be wrapped in <Popper />');
  }

  return context;
};

export const Popper = ({ children, ...options }: { children: React.ReactNode } & PopperOptions) => {
  const arrowRef = useRef(null);
  options.arrowRef = arrowRef;
  const popper = usePopper(options);
  //@ts-expect-error adding arrow ref to popper
  popper.arrowRef = arrowRef;

  return <PopperContext.Provider value={popper}>{children}</PopperContext.Provider>;
};

interface PopperTriggerProps extends React.HTMLProps<HTMLElement> {
  openOnClick?: boolean;
  asChild?: boolean;
}

export const PopperTrigger = React.forwardRef<HTMLElement, PopperTriggerProps>(
  ({ openOnClick = true, ...props }, propRef) => {
    const context = usePopperContext();
    const ref = useMergeRefs([context.refs.setReference, propRef]);

    useEffect(() => {
      if (propRef?.current) {
        propRef.current.context = context;
      }
    }, [propRef]);

    return (
      <span
        ref={ref}
        onClick={() => {
          if (!openOnClick) {
            return;
          }
          context.setOpen(!context.open);
        }}
        // The user can style the trigger based on the state
        data-state={context.open ? 'open' : 'closed'}
        {...context.getReferenceProps(props)}
        className={cx('cursor-pointer', props?.className)}
      >
        {props.children}
      </span>
    );
  }
);

export const PopperContent = React.forwardRef<HTMLDivElement, PopperContentProps>(
  function PopperContent(
    {
      style,
      headerText,
      iconRequired = true,
      noHeader = false,
      showArrow = true,
      onClose,
      className,
      ...props
    },
    propRef
  ) {
    const context = usePopperContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);
    const transition = useTransitionStyles(context?.context, {
      duration: 500
    });

    const referenceHidden =
      context?.middlewareData?.hide?.referenceHidden || context?.middlewareData?.hide?.escaped;

    const arrowColor = props?.arrowColor || 'var(--tooltip-bg-color';

    if (!context.open) return null;

    const closePopper = () => {
      context.setOpen(false);
    };

    const openPopper = () => {
      context.setOpen(true);
    };

    return (
      <FloatingPortal>
        {transition?.isMounted && (
          <div
            ref={ref}
            style={{
              ...context?.floatingStyles,
              ...style,
              ...transition?.styles,
              visibility: referenceHidden ? 'hidden' : 'visible'
            }}
            className={className}
            {...context?.getFloatingProps(props)}
          >
            {!noHeader ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px'
                }}
              >
                <div>{headerText}</div>
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (onClose) onClose();
                    context?.setOpen(false);
                  }}
                >
                  {iconRequired && <CloseIcon />}
                </div>
              </div>
            ) : null}
            {typeof props.children === 'function'
              ? props?.children({ closePopper: closePopper, openPopper: openPopper })
              : props.children}
            {showArrow && (
              <FloatingArrow context={context} ref={context?.arrowRef} fill={arrowColor} />
            )}
          </div>
        )}
      </FloatingPortal>
    );
  }
);
PopperTrigger.displayName = 'PopperTrigger';
